/**
 * Firestore data-access service. Generic CRUD + query helpers that
 * feature-level services (students, mentors, classes) build on top of.
 * Keeping raw Firestore calls here keeps query logic out of components.
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment,
} from 'firebase/firestore';

import { db } from './config';

/** Map a Firestore snapshot doc into a plain `{ id, ...data }` object. */
export function mapDoc(snapshot) {
  return { id: snapshot.id, ...snapshot.data() };
}

/** Map a query snapshot into an array of `{ id, ...data }`. */
export function mapDocs(querySnapshot) {
  return querySnapshot.docs.map(mapDoc);
}

export const firestoreService = {
  // ---- Reads ---------------------------------------------------------------
  async getDocument(path, id) {
    const snap = await getDoc(doc(db, path, id));
    return snap.exists() ? mapDoc(snap) : null;
  },

  async getCollection(path, constraints = []) {
    const ref = constraints.length
      ? query(collection(db, path), ...constraints)
      : collection(db, path);
    return mapDocs(await getDocs(ref));
  },

  /**
   * Server-side count of a collection/query via Firestore aggregation. Reads a
   * single aggregation result instead of downloading every matching document —
   * the cheap way to get totals (e.g. dashboard headline metrics).
   */
  async count(path, constraints = []) {
    const ref = constraints.length
      ? query(collection(db, path), ...constraints)
      : collection(db, path);
    const snap = await getCountFromServer(ref);
    return snap.data().count;
  },

  /**
   * Subscribe to a single document in realtime. `onData` receives the mapped
   * `{ id, ...data }` object, or null while the doc doesn't exist. Returns the
   * unsubscribe fn.
   */
  subscribeDocument(path, id, onData, onError) {
    return onSnapshot(
      doc(db, path, id),
      (snap) => onData(snap.exists() ? mapDoc(snap) : null),
      (err) => onError?.(err)
    );
  },

  /**
   * Subscribe to a collection in realtime. Returns the unsubscribe fn.
   * `constraints` is an array of Firestore query constraints (where/orderBy).
   */
  subscribeCollection(path, constraints, onData, onError) {
    const ref = constraints?.length
      ? query(collection(db, path), ...constraints)
      : collection(db, path);
    return onSnapshot(
      ref,
      (snap) => onData(mapDocs(snap)),
      (err) => onError?.(err)
    );
  },

  // ---- Writes --------------------------------------------------------------
  /** Create a doc with an auto-generated id; returns the new id. */
  async addDocument(path, data) {
    const ref = await addDoc(collection(db, path), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  },

  /**
   * Atomically create many documents in one collection via a write batch.
   * Each gets server `createdAt`/`updatedAt`. Returns the new ids in order.
   * Batches are capped at 500 ops by Firestore — callers should chunk beyond that.
   */
  async createManyDocuments(path, items) {
    const batch = writeBatch(db);
    const ids = [];
    for (const data of items) {
      const ref = doc(collection(db, path));
      ids.push(ref.id);
      batch.set(ref, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    }
    await batch.commit();
    return ids;
  },

  /**
   * Atomically update many existing documents in one collection via write
   * batches. `updates` is `[{ id, data }]`; each gets a server `updatedAt`.
   * Chunked at 450 ops to stay under Firestore's 500-op batch cap.
   */
  async updateManyDocuments(path, updates) {
    const CHUNK = 450;
    for (let i = 0; i < updates.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const { id, data } of updates.slice(i, i + CHUNK)) {
        batch.update(doc(db, path, id), { ...data, updatedAt: serverTimestamp() });
      }
      await batch.commit();
    }
  },

  /**
   * Delete many documents from one collection via write batches, chunked at
   * 450 ops. Deleting an id that doesn't exist is a no-op, so callers may pass
   * speculative ids (e.g. session-log stubs that were never created).
   */
  async deleteManyDocuments(path, ids) {
    const CHUNK = 450;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = writeBatch(db);
      for (const id of ids.slice(i, i + CHUNK)) {
        batch.delete(doc(db, path, id));
      }
      await batch.commit();
    }
  },

  setDocument(path, id, data) {
    return setDoc(doc(db, path, id), data, { merge: true });
  },

  updateDocument(path, id, data) {
    return updateDoc(doc(db, path, id), { ...data, updatedAt: serverTimestamp() });
  },

  deleteDocument(path, id) {
    return deleteDoc(doc(db, path, id));
  },

  /** Append `value` to an array field (deduped by Firestore's arrayUnion). */
  addToArray(path, id, field, value) {
    return updateDoc(doc(db, path, id), {
      [field]: arrayUnion(value),
      updatedAt: serverTimestamp(),
    });
  },

  /** Remove an exact-match `value` from an array field. */
  removeFromArray(path, id, field, value) {
    return updateDoc(doc(db, path, id), {
      [field]: arrayRemove(value),
      updatedAt: serverTimestamp(),
    });
  },
};

// Re-export the query builders so feature services don't import firebase
// directly — one place owns the Firestore dependency surface.
export { where, orderBy, limit, serverTimestamp, Timestamp, arrayUnion, arrayRemove, increment };

export default firestoreService;
